import Phaser from 'phaser';
import { AABB } from '../engine/CollisionGrid';

export type CollectibleType = 'coin' | 'floppy' | 'keyYellow' | 'keyRed' | 'keyBlue' | 'ammo';

export interface CollectibleConfig {
  type: CollectibleType;
  x: number;
  y: number;
  value?: number;
}

/**
 * Collectible items (coins, floppy disks, keys, ammo)
 */
export class Collectible {
  private sprite: Phaser.GameObjects.Sprite | null = null;
  private x: number;
  private y: number;
  private type: CollectibleType;
  private value: number;
  private collected: boolean = false;
  private bobOffset: number = 0;
  private bobSpeed: number = 0.003;

  // Hitbox
  private width: number = 12;
  private height: number = 12;

  constructor(config: CollectibleConfig) {
    this.x = config.x;
    this.y = config.y;
    this.type = config.type;
    this.value = config.value ?? this.getDefaultValue();
    this.bobOffset = Math.random() * Math.PI * 2; // Random start phase
  }

  private getDefaultValue(): number {
    switch (this.type) {
      case 'coin': return 100;
      case 'floppy': return 1000;
      case 'ammo': return 5;
      default: return 0;
    }
  }

  init(scene: Phaser.Scene): void {
    this.sprite = scene.add.sprite(this.x, this.y, this.type);
    this.sprite.setOrigin(0, 0);
  }

  update(time: number): void {
    if (this.collected || !this.sprite) return;

    // Bobbing animation
    const bob = Math.sin(time * this.bobSpeed + this.bobOffset) * 2;
    this.sprite.y = this.y + bob;
  }

  getHitbox(): AABB {
    return {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
    };
  }

  collect(): { type: CollectibleType; value: number } | null {
    if (this.collected) return null;

    this.collected = true;
    if (this.sprite) {
      // Collection animation
      this.sprite.scene.tweens.add({
        targets: this.sprite,
        y: this.sprite.y - 20,
        alpha: 0,
        scale: 1.5,
        duration: 200,
        onComplete: () => {
          this.sprite?.destroy();
        },
      });
    }

    return { type: this.type, value: this.value };
  }

  isCollected(): boolean {
    return this.collected;
  }

  getType(): CollectibleType {
    return this.type;
  }
}
